import { assert } from "@/core";
import { BaseService } from "./base.service";
import { ResponseWrapper } from "./util";

export class UsersService extends BaseService {
  static get entity() {
    return "users";
  }

  /**
   * Get current authenticated user
   * @returns {Promise<ResponseWrapper>}
   */
  static async getCurrent() {
    const response = await this.request({ auth: true }).get(
      `${this.entity}/current`
    );
    return new ResponseWrapper(response, response.data.data);
  }

  /**
   * Get all users
   * @param {Object} parameters - Query parameters (e.g. hospitalId, role, page)
   * @returns {Promise<ResponseWrapper>}
   */
  static async getAll(parameters = {}) {
    assert.object(parameters);

    const params = { page: 1, limit: 50, ...parameters };

    const response = await this.request({ auth: true }).get(
      "admin/users",
      { params }
    );

    return new ResponseWrapper(response, response);
  }

  /**
   * Get a user by ID
   * @param {string} id
   * @returns {Promise<ResponseWrapper>}
   */
  static async getById(id) {
    assert.string(id, { required: true });

    const response = await this.request({ auth: true }).get(
      `${this.entity}/${id}`
    );

    return new ResponseWrapper(response, response?.data?.data);
  }

  /**
   * Create a new user
   * @param {Object} data
   * @returns {Promise<ResponseWrapper>}
   */
  static async create(data = {}) {
    assert.object(data, { required: true });

    const response = await this.request({ auth: true }).post(
      "admin/users",
      data
    );

    return new ResponseWrapper(response, response);
  }

  /**
   * Update a user
   * @param {string} id
   * @param {Object} data
   * @returns {Promise<ResponseWrapper>}
   */
  static async update(id, data = {}) {
    assert.string(id, { required: true });
    assert.object(data, { required: true });

    const response = await this.request({ auth: true }).patch(
      `admin/users/${id}`,
      data
    );

    return new ResponseWrapper(response, response);
  }

  /**
   * Delete a user
   * @param {string} id
   * @returns {Promise<ResponseWrapper>}
   */
  static async delete(id) {
    assert.string(id, { required: true });

    const response = await this.request({ auth: true }).delete(
      `admin/users/${id}`
    );

    return new ResponseWrapper(response, response);
  }

  /**
   * Reset a user's password
   * @param {string} id
   * @param {string} password
   * @returns {Promise<ResponseWrapper>}
   */
  static async resetPassword(id, password) {
    assert.string(id, { required: true });
    assert.string(password, { required: true });

    const response = await this.request({ auth: true }).post(
      `admin/users/${id}/reset-password`,
      { password }
    );

    return new ResponseWrapper(response, response);
  }

  /**
   * Update user status (active / inactive)
   * @param {string} id
   * @param {boolean} isActive
   * @returns {Promise<ResponseWrapper>}
   */
  static async updateStatus(id, isActive) {
    assert.string(id, { required: true });

    const response = await this.request({ auth: true }).patch(
      `${this.entity}/${id}/status`,
      { isActive }
    );

    return new ResponseWrapper(response, response?.data?.data);
  }
}
